import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useFetch } from "../../../../utils/hooks/useFetch";
import {
  deleteProjectEngagementReqConfig,
  getProjectsListServiceConfig,
} from "../../../../utils/services/projects";
import moment from "moment";
import { getQueryParams } from "../../../../utils/helpers";
import { translate } from "../../../../utils/translations/translate";
import { Link } from "react-router-dom";
import {
  DATE_TIME_FORMAT,
  DEFAULT_PAGE_SIZE,
} from "../../../../utils/constants";
import { useCachedResults } from "../../../../utils/hooks/useCachedResults";
import {
  exportProjectPdfData,
  generateProjectsExcelReport,
} from "../../../../utils/reports";
import { useToastInfo } from "../../../../utils/hooks/useToastInfo";
export const useProjectsList = () => {
  const { isLoading, performAction } = useFetch();
  const { toastInfo, setToastInfo } = useToastInfo();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const { getCachedResult, setCachedResult } = useCachedResults();
  const [projectsList, setProjectsList] = useState([]);
  const [projectKey, setProjectKey] = useState("");
  const [resourceKey, setResourceKey] = useState("");
  const [statusKey, setStatusKey] = useState("Active");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const gridRef = useRef(null);
  const handleToastClose = useCallback(() => {
    setToastInfo(null);
  }, [setToastInfo]);
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: translate("columnDefs.project.id"),
        field: "projectId",
        sortable: true,
        filter: false,
        width: 90,
      },
      {
        headerName: translate("columnDefs.project.name"),
        field: "projectName",
        sortable: true,
        filter: false,
        width: 120,
        cellRenderer: (params) => {
          const { data, value } = params;
          const { projectId } = data;
          return <Link to={`/p/update/${projectId}`}>{value}</Link>;
        },
      },
      {
        headerName: translate("columnDefs.project.resource"),
        field: "resourceName",
        sortable: true,
        filter: false,
        width: 120,
        cellRenderer: (params) => {
          const { data, value } = params;
          const { resourceId } = data;
          return <Link to={`/u/${resourceId}`}>{value}</Link>;
        },
      },
      {
        headerName: translate("columnDefs.project.client"),
        field: "client",
        sortable: true,
        filter: false,
        width: 100,
      },
      {
        headerName: translate("columnDefs.project.address"),
        field: "location",
        sortable: true,
        filter: false,
        width: 100,
      },
      {
        headerName: translate("columnDefs.project.primeVendor"),
        field: "primeVendor",
        sortable: true,
        filter: false,
        width: 100,
      },
      {
        headerName: translate("columnDefs.project.poStartDate"),
        field: "poStartDate",
        sortable: true,
        filter: false,
        width: 100,
      },
      {
        headerName: translate("columnDefs.project.poEndDate"),
        field: "poEndDate",
        sortable: true,
        filter: false,
        width: 100,
      },
      {
        headerName: translate("columnDefs.project.paymentTerms"),
        field: "paymentTerms",
        sortable: true,
        filter: false,
        width: 120,
      },
    ];
  }, []);
  const fetchProjectsListData = useCallback(
    async (queryString) => {
      try {
        const { url, options } = getProjectsListServiceConfig(queryString);
        const responseData = await performAction(url, options);
        if (Array.isArray(responseData?.Result)) {
          return responseData.Result;
        }
        return [];
      } catch (e) {
        console.error("Error fetching projects");
        setToastInfo({
          message: e.message,
          type: "error",
          autoExpire: true,
        });
        return [];
      }
    },
    [performAction, setToastInfo]
  );
  const handleGridRowSelection = useCallback(({ node }) => {
    const { data } = node;
    const isSelected = node.isSelected();
    setSelectedRecords((prev) => {
      let updatedData = [...prev];
      if (prev.includes(data.projectId) && !isSelected) {
        updatedData = updatedData.filter((dt) => dt !== data.projectId);
      } else {
        if (isSelected) {
          updatedData.push(data.projectId);
        }
      }
      return updatedData;
    });
  }, []);
  const formatProjectsListData = useCallback((data) => {
    const formattedList = data.map((prj) => {
      const {
        ProjectID,
        ProjectName,
        ResourceName,
        ResourceID,
        ClientName,
        ClientAddress,
        VendorName,
        POStartDate,
        POEndDate,
        PaymentTerms,
      } = prj;
      return {
        projectId: ProjectID,
        projectName: ProjectName,
        resourceName: ResourceName,
        resourceId: ResourceID,
        client: ClientName,
        location: ClientAddress || "N/A",
        primeVendor: VendorName || "N/A",
        poStartDate: POStartDate
          ? moment(POStartDate).format(DATE_TIME_FORMAT.DISPLAY)
          : "N/A",
        poEndDate: POEndDate
          ? moment(POEndDate).format(DATE_TIME_FORMAT.DISPLAY)
          : "N/A",
        paymentTerms: PaymentTerms,
      };
    });
    return formattedList;
  }, []);
  const fetchProjectsList = useCallback(
    async (queryString) => {
      try {
        const responseData = await fetchProjectsListData(queryString);
        if (Array.isArray(responseData)) {
          const formattedList = formatProjectsListData(responseData);
          setProjectsList(formattedList);
          setTotalRecords(responseData[0]?.TotalRecords || 0);
        }
      } catch (e) {
        console.error("Error fetching projects");
      }
    },
    [fetchProjectsListData, formatProjectsListData]
  );
  const handlePaginationChange = useCallback(({ page, pageSize }) => {
    setPageNumber(page);
    setPageSize(pageSize);
  }, []);
  const handleDeletePopupClose = useCallback(() => {
    setShowDeleteModal(false);
  }, []);
  const beforeDeleteProject = useCallback(() => {
    setShowDeleteModal(true);
  }, []);
  const onDeleteProject = async () => {
    try {
      setShowDeleteModal(false);
      const { url, options } =
        deleteProjectEngagementReqConfig(selectedRecords);
      const response = await performAction(url, options);
      if (response?.StatusCode === 200) {
        setToastInfo({
          type: "info",
          message: translate("labels.deletePrjToast"),
          autoExpire: true,
        });
        const additionalParams = {
          PageNo: pageNumber,
          PageSize: pageSize,
          Status: statusKey,
          Project: projectKey,
          Resource: resourceKey,
        };
        const queryParams = getQueryParams(additionalParams);
        await fetchProjectsList(queryParams);
        return true;
      }
      throw new Error("Soemthing went wrong Unable to delete the project(s)");
    } catch (e) {
      console.error("Error deleting project tasks", e);
      setToastInfo({
        type: "error",
        message: e.message,
        autoExpire: true,
      });
    }
  };
  const getFilteredResponseToDownload = (response) => {
    const gridSelectedRows =
      gridRef.current?.gridAPi?.getSelectedRows().map((row) => row.projectId) ||
      [];
    if (gridSelectedRows?.length) {
      const filteredResponse = response.filter((res) =>
        gridSelectedRows.includes(res.ProjectID)
      );
      return filteredResponse;
    }
    return response;
  };
  const downloadDataForReportsGeneration = async (format) => {
    try {
      const additionalParams = {
        Status: statusKey,
        Project: projectKey,
        Resource: resourceKey,
      };
      const queryString = getQueryParams(additionalParams);
      const cachedResponse = getCachedResult(queryString);
      if (cachedResponse) {
        format === "excel"
          ? generateProjectsExcelReport(
              getFilteredResponseToDownload(cachedResponse)
            )
          : exportProjectPdfData(getFilteredResponseToDownload(cachedResponse));
        return true;
      }
      const response = await fetchProjectsListData(queryString);
      if (Array.isArray(response) && response.length) {
        setCachedResult(queryString, response);
        format === "excel"
          ? generateProjectsExcelReport(getFilteredResponseToDownload(response))
          : exportProjectPdfData(getFilteredResponseToDownload(response));
      }
    } catch (e) {
      console.error("Error generating reports");
    }
  };
  useEffect(() => {
    setPageNumber(1);
  }, [projectKey, resourceKey, statusKey]);
  useEffect(() => {
    const additionalParams = {
      PageNo: pageNumber,
      PageSize: pageSize,
      Status: statusKey,
      Project: projectKey,
      Resource: resourceKey,
    };
    const queryParams = getQueryParams(additionalParams);
    let delay = 0;
    if (additionalParams.Project || additionalParams.Resource) {
      delay = 1500;
    }
    const fetchTimer = setTimeout(() => {
      fetchProjectsList(queryParams);
    }, delay);
    return () => {
      clearTimeout(fetchTimer);
    };
  }, [
    fetchProjectsList,
    pageNumber,
    pageSize,
    statusKey,
    projectKey,
    resourceKey,
  ]);
  return {
    toastInfo,
    isLoading,
    projectsList,
    projectKey,
    resourceKey,
    columnDefs,
    setProjectKey,
    setResourceKey,
    statusKey,
    setStatusKey,
    pageNumber,
    pageSize,
    gridRef,
    selectedRecords,
    handlePaginationChange,
    downloadDataForReportsGeneration,
    handleToastClose,
    handleGridRowSelection,
    onDeleteProject,
    beforeDeleteProject,
    handleDeletePopupClose,
    showDeleteModal,
    totalRecords,
  };
};
